<script lang="ts" module>
  /** Percentage change from previous to current (null when not comparable). */
  export function percentChange(current: number | null | undefined, previous: number | null | undefined): number | null {
    if (current === null || current === undefined || previous === null || previous === undefined) return null;
    if (previous === 0) return current === 0 ? 0 : 100;
    return ((current - previous) / Math.abs(previous)) * 100;
  }
</script>

<script lang="ts">
  // Change vs. a previous period, as quiet coloured text ("+12%").
  //   <Delta current={hours} previous={lastPeriodHours} />
  //   <Delta value={12.5} />                       a precomputed % change
  //   <Delta current={costs} previous={…} invert />  going down is good
  // Green when good, red when bad, grey when (almost) flat (< `flat` %).
  import '../messages/ui';
  import { TrendingUp, TrendingDown, Minus } from '@lucide/svelte';
  import { formatPercent } from '../format';
  import { t } from '../i18n.svelte';

  interface Props {
    /** Precomputed percentage change. Overrides current/previous. */
    value?: number | null;
    current?: number | null;
    previous?: number | null;
    /** For costs, outstanding money etc.: a decrease is good. */
    invert?: boolean;
    /** Changes smaller than this (in %) count as flat. */
    flat?: number;
    format?: (absPercent: number) => string;
    size?: 'sm' | 'md';
    /** Pill with a trend icon instead of plain text (dense tables). */
    pill?: boolean;
  }
  let { value, current, previous, invert = false, flat = 0.5, format = (v) => formatPercent(v), size = 'md', pill = false }: Props = $props();

  const delta = $derived(value !== undefined ? value : percentChange(current, previous));
  const isFlat = $derived(delta !== null && Math.abs(delta) < flat);
  const good = $derived(delta === null || isFlat ? null : invert ? delta < 0 : delta > 0);
  const text = $derived(delta === null ? '' : format(Math.abs(delta)));
  const label = $derived(delta === null ? '' : isFlat ? t('ui.trend_flat') : delta > 0 ? t('ui.trend_up', { value: text }) : t('ui.trend_down', { value: text }));
</script>

{#if delta !== null}
  <span class="delta {size}" class:pill class:good={good === true} class:bad={good === false} title={label}>
    {#if pill}{#if isFlat}<Minus aria-hidden="true" />{:else if delta > 0}<TrendingUp aria-hidden="true" />{:else}<TrendingDown aria-hidden="true" />{/if}{/if}
    <span class="sr-only">{label}</span>
    <span aria-hidden="true">{isFlat ? '±0%' : `${delta > 0 ? '+' : '−'}${text}`}</span>
  </span>
{/if}

<style>
  .delta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    color: var(--text-3);
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    font-variant-numeric: tabular-nums;
    line-height: var(--leading-snug);
    white-space: nowrap;
  }
  .sm { font-size: var(--text-2xs); }
  .good { color: var(--success); }
  .bad { color: var(--danger); }
  .pill { padding: var(--space-0_5) var(--space-2); border-radius: var(--radius-pill); background: var(--surface-3); color: var(--text-2); }
  .pill :global(svg) { width: var(--icon-xs); height: var(--icon-xs); }
  .pill.good { background: var(--success-soft); color: var(--success-strong); }
  .pill.bad { background: var(--danger-soft); color: var(--danger-strong); }
</style>
